import { useState, useRef, useEffect } from "react";

// ═══════════════════════════════════════
// IMMERSE CERTIFIED DESIGNER PORTAL
// designers.immersecontrol.com
// ═══════════════════════════════════════

const BLUE = "#1E90FF";
const AMBER = "#FFC043";
const GREEN = "#21C55D";
const RED = "#E63946";
const BG = "#0a0a0f";
const SURFACE = "#12121a";
const SURFACE2 = "#1a1a24";
const BORDER = "#2a2a36";
const TEXT = "#e0e0e8";
const DIM = "#666680";
const MONO = "'IBM Plex Mono', monospace";
const SANS = "'IBM Plex Sans', sans-serif";

// Example show data
const SHOWS = [
  { name: "Prison Break", rooms: 4, nodes: 16, type: "Escape Room", fixtures: 18, universes: 4, sequences: 6, color: RED,
    desc: "4-room sequential escape room. Intake processing → Cell Block → Exercise Yard → Escape Tunnel. 60-minute timed gameplay with logical puzzle progression, mag locks, animatronic PLC, and full E-Stop safety.",
    stats: [{l:"GPIO",v:4},{l:"Relay",v:4},{l:"DMX",v:4},{l:"Audio",v:2},{l:"Video",v:1},{l:"Serial",v:1}] },
  { name: "Carnival of Screams", rooms: 7, nodes: 28, type: "Haunted House", fixtures: 29, universes: 7, sequences: 9, color: AMBER,
    desc: "7-room walk-through clown haunted house. Ticket Booth → Big Top → Mirror Maze → Funhouse → Backstage → Sewer → Chainsaw Exit. Continuous flow with actor-assisted scares, air cannons, and spinning tunnel.",
    stats: [{l:"GPIO",v:7},{l:"Relay",v:7},{l:"DMX",v:7},{l:"Audio",v:3},{l:"Video",v:2},{l:"Serial",v:2}] },
  { name: "Threshold", rooms: 20, nodes: 73, type: "Immersive Experience", fixtures: 80, universes: 20, sequences: 22, color: BLUE,
    desc: "20-room Meow Wolf-style interdimensional experience across 8 worlds. Non-linear exploration with portals connecting Space Station, Ancient Egypt, Deep Sea, Post-Apocalypse, Fairy Tale, Time Travel, and The Rift convergence.",
    stats: [{l:"GPIO",v:20},{l:"Relay",v:20},{l:"DMX",v:20},{l:"Audio",v:6},{l:"Video",v:4},{l:"Serial",v:3}] },
];

const TOOLS = [
  { id: "patch", icon: "🔌", name: "Network Patching Sheet", desc: "Full IP schedule, switch port map, DMX patch, I/O wiring, serial, A/V, and sign-off checklist. Print to PDF for venue handoff.", tag: "INTERACTIVE" },
  { id: "calc", icon: "🧮", name: "Node Calculator", desc: "Enter your room count and types → get exact node quantities, IP address plan, switch port requirements, and power budget.", tag: "CALCULATOR" },
  { id: "dmx", icon: "💡", name: "DMX Address Planner", desc: "Plan DMX universes and addresses. Enter fixtures → auto-assign addresses avoiding conflicts. Export as CSV or patch sheet.", tag: "PLANNER" },
  { id: "cable", icon: "📏", name: "Cable Estimator", desc: "Enter room distances from rack → get total cable lengths, labeled cable schedule, and cut list for installation day.", tag: "ESTIMATOR" },
  { id: "valid", icon: "✓", name: "Show File Validator", desc: "Upload a .ishow file → checks for missing nodes, orphan references, address conflicts, safety gaps, and schema compliance.", tag: "VALIDATOR" },
  { id: "bom", icon: "📋", name: "BOM Generator", desc: "Select room count, attraction type, and features → generates a complete bill of materials with quantities and categories.", tag: "GENERATOR" },
];

const NODE_TYPES = [
  { name: "GPIO-8IO", role: "Input", desc: "8-channel dry contact input. Sensors, buttons, E-stops, reed switches.", hw: "ESP32-WROOM-32 + W5500 Ethernet", power: "24V DC, 2W", color: "#22aa88" },
  { name: "RELAY-8CH", role: "Output", desc: "8-channel relay output. Mag locks, solenoids, fog machines, motors. Fault policy: halt.", hw: "ESP32-WROOM-32 + W5500 Ethernet", power: "24V DC, 5W + load", color: RED },
  { name: "DMX Node", role: "Lighting", desc: "sACN/E1.31 to DMX512 output. One universe per node. Supports all standard fixtures.", hw: "ESP32-WROOM-32 + W5500 Ethernet", power: "24V DC, 2W", color: "#aa44ff" },
  { name: "Audio Node", role: "Audio", desc: "8-channel audio playback. WAV/MP3 files via USB audio interface. Ambient loops + triggered one-shots.", hw: "Raspberry Pi 5 4GB + USB DAC", power: "5V USB-C, 15W", color: AMBER },
  { name: "Video Node", role: "Video", desc: "HDMI video output for projections and displays. Looping playback + triggered clips.", hw: "Raspberry Pi 5 4GB + HDMI", power: "5V USB-C, 15W", color: "#ff6688" },
  { name: "Serial Bridge", role: "Serial", desc: "Ethernet to RS-232/RS-485 bridge. Connects to animatronic PLCs, motor controllers, and legacy devices.", hw: "ESP32-WROOM-32 + W5500 + MAX232", power: "24V DC, 2W", color: "#8888aa" },
];

// ═══ COMPONENTS ═══

function Badge({children, color}) {
  return <span style={{display:"inline-block",padding:"2px 8px",borderRadius:3,fontSize:9,fontWeight:700,fontFamily:MONO,letterSpacing:".08em",color,border:`1px solid ${color}40`,background:`${color}10`}}>{children}</span>;
}

function Stat({label, value, color}) {
  return <div style={{textAlign:"center"}}>
    <div style={{fontSize:28,fontWeight:700,fontFamily:MONO,color:color||BLUE,lineHeight:1}}>{value}</div>
    <div style={{fontSize:9,color:DIM,fontFamily:MONO,marginTop:4,letterSpacing:".05em",textTransform:"uppercase"}}>{label}</div>
  </div>;
}

// ═══ NODE CALCULATOR TOOL ═══
function NodeCalcTool() {
  const [rooms, setRooms] = useState(4);
  const [hasVideo, setHasVideo] = useState(true);
  const [hasSerial, setHasSerial] = useState(true);
  const [type, setType] = useState("escape");

  const gpio = rooms;
  const relay = rooms;
  const dmx = rooms;
  const audio = Math.max(1, Math.ceil(rooms / 3.5));
  const video = hasVideo ? Math.max(1, Math.ceil(rooms / 5)) : 0;
  const serial = hasSerial ? Math.max(1, Math.ceil(rooms / 7)) : 0;
  const total = gpio + relay + dmx + audio + video + serial;
  const switchPorts = total + 3; // +runtime +OCC +headroom
  const switchSize = switchPorts <= 24 ? 24 : switchPorts <= 48 ? 48 : "48+24 cascade";

  return <div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20}}>
      <div>
        <label style={{fontSize:10,color:DIM,fontFamily:MONO,display:"block",marginBottom:4}}>ROOM COUNT</label>
        <input type="number" min={1} max={50} value={rooms} onChange={e=>setRooms(Math.max(1,parseInt(e.target.value)||1))}
          style={{width:"100%",background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"8px 10px",borderRadius:4,fontFamily:MONO,fontSize:16,fontWeight:700}} />
      </div>
      <div>
        <label style={{fontSize:10,color:DIM,fontFamily:MONO,display:"block",marginBottom:4}}>ATTRACTION TYPE</label>
        <select value={type} onChange={e=>setType(e.target.value)}
          style={{width:"100%",background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"8px 10px",borderRadius:4,fontFamily:MONO,fontSize:12}}>
          <option value="escape">Escape Room</option>
          <option value="haunt">Haunted House</option>
          <option value="immersive">Immersive Experience</option>
          <option value="darkride">Dark Ride</option>
        </select>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:6,paddingTop:18}}>
        <label style={{fontSize:11,color:TEXT,fontFamily:MONO,cursor:"pointer"}}><input type="checkbox" checked={hasVideo} onChange={e=>setHasVideo(e.target.checked)} style={{marginRight:6}} />Video Nodes</label>
        <label style={{fontSize:11,color:TEXT,fontFamily:MONO,cursor:"pointer"}}><input type="checkbox" checked={hasSerial} onChange={e=>setHasSerial(e.target.checked)} style={{marginRight:6}} />Serial / PLC</label>
      </div>
    </div>
    <div style={{background:BG,borderRadius:6,padding:16,border:`1px solid ${BORDER}`}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:16}}>
        <Stat label="GPIO-8IO" value={gpio} color="#22aa88" />
        <Stat label="RELAY-8CH" value={relay} color={RED} />
        <Stat label="DMX Node" value={dmx} color="#aa44ff" />
        <Stat label="Audio Node" value={audio} color={AMBER} />
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16}}>
        <Stat label="Video Node" value={video} color="#ff6688" />
        <Stat label="Serial Bridge" value={serial} color="#8888aa" />
        <Stat label="Total Nodes" value={total} color={BLUE} />
        <Stat label="Switch" value={switchSize} color={TEXT} />
      </div>
    </div>
    <div style={{marginTop:12,padding:12,background:`${BLUE}08`,borderRadius:4,border:`1px solid ${BLUE}20`}}>
      <div style={{fontSize:10,color:BLUE,fontFamily:MONO,fontWeight:600,marginBottom:6}}>IP PLAN</div>
      <div style={{fontSize:11,color:DIM,fontFamily:MONO,lineHeight:1.8}}>
        10.0.0.1 — Switch &nbsp;|&nbsp; 10.0.1.1 — Runtime &nbsp;|&nbsp; 10.0.1.2 — OCC<br/>
        10.0.2.10–{10+gpio-1} — GPIO &nbsp;|&nbsp; 10.0.2.{40}–{40+relay-1} — Relay<br/>
        10.0.3.10–{10+dmx-1} — DMX &nbsp;|&nbsp; 10.0.4.10–{10+audio-1} — Audio
        {video > 0 && <><br/>10.0.5.10–{10+video-1} — Video</>}
        {serial > 0 && <><br/>10.0.6.10–{10+serial-1} — Serial</>}
      </div>
    </div>
  </div>;
}

// ═══ DMX ADDRESS PLANNER ═══
function DMXPlannerTool() {
  const [fixtures, setFixtures] = useState([
    {name:"Wash Par",ch:6,qty:8},{name:"Strobe",ch:4,qty:4},{name:"Pixel Bar",ch:120,qty:2},{name:"Moving Spot",ch:33,qty:1},{name:"Fog Machine",ch:5,qty:1}
  ]);
  const addFix = () => setFixtures([...fixtures, {name:"New Fixture",ch:6,qty:1}]);
  const update = (i,k,v) => setFixtures(fixtures.map((f,j)=>j===i?{...f,[k]:v}:f));
  const remove = (i) => setFixtures(fixtures.filter((_,j)=>j!==i));

  let addr = 1, u = 1;
  const plan = [];
  fixtures.forEach(f => {
    for(let q=0; q<f.qty; q++) {
      if(addr + f.ch - 1 > 512) { u++; addr=1; }
      plan.push({name:`${f.name} ${f.qty>1?q+1:""}`.trim(), u, start:addr, end:addr+f.ch-1, ch:f.ch});
      addr += f.ch;
    }
  });

  return <div>
    <div style={{fontSize:10,color:DIM,fontFamily:MONO,marginBottom:10}}>Define fixtures below. Addresses auto-assign avoiding conflicts.</div>
    {fixtures.map((f,i) => <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr auto",gap:8,marginBottom:6}}>
      <input value={f.name} onChange={e=>update(i,"name",e.target.value)} style={{background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"6px 8px",borderRadius:3,fontFamily:MONO,fontSize:11}} />
      <input type="number" value={f.ch} onChange={e=>update(i,"ch",parseInt(e.target.value)||1)} style={{background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"6px 8px",borderRadius:3,fontFamily:MONO,fontSize:11}} placeholder="Ch" />
      <input type="number" value={f.qty} onChange={e=>update(i,"qty",parseInt(e.target.value)||1)} style={{background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"6px 8px",borderRadius:3,fontFamily:MONO,fontSize:11}} placeholder="Qty" />
      <button onClick={()=>remove(i)} style={{background:"transparent",border:"none",color:RED,cursor:"pointer",fontSize:14}}>✕</button>
    </div>)}
    <button onClick={addFix} style={{background:"transparent",border:`1px dashed ${BORDER}`,color:BLUE,padding:"6px 14px",borderRadius:3,fontFamily:MONO,fontSize:10,cursor:"pointer",marginBottom:14}}>+ Add Fixture Type</button>
    <div style={{background:BG,borderRadius:6,overflow:"hidden",border:`1px solid ${BORDER}`}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
        <thead><tr style={{background:SURFACE2}}>
          {["#","Fixture","U","Start","End","Ch"].map(h=><th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:9,color:DIM,fontWeight:700,letterSpacing:".06em",borderBottom:`1px solid ${BORDER}`}}>{h}</th>)}
        </tr></thead>
        <tbody>{plan.map((p,i)=><tr key={i} style={{borderBottom:`1px solid ${BORDER}15`}}>
          <td style={{padding:"5px 10px",color:DIM}}>{i+1}</td>
          <td style={{padding:"5px 10px",color:TEXT}}>{p.name}</td>
          <td style={{padding:"5px 10px",color:"#aa44ff",fontWeight:600}}>{p.u}</td>
          <td style={{padding:"5px 10px",color:GREEN}}>{String(p.start).padStart(3,"0")}</td>
          <td style={{padding:"5px 10px",color:AMBER}}>{String(p.end).padStart(3,"0")}</td>
          <td style={{padding:"5px 10px",color:DIM}}>{p.ch}</td>
        </tr>)}</tbody>
      </table>
    </div>
    <div style={{marginTop:8,fontSize:10,color:DIM,fontFamily:MONO}}>{plan.length} fixtures across {u} universe{u>1?"s":""} · {plan.reduce((a,p)=>a+p.ch,0)} total channels</div>
  </div>;
}

// ═══ BOM GENERATOR ═══
function BOMTool() {
  const [rooms, setRooms] = useState(6);
  const [parsPerRoom, setPars] = useState(3);
  const [hasStrobe, setStrobe] = useState(true);
  const [hasFog, setFog] = useState(true);
  const [hasPixel, setPixel] = useState(true);
  const [hasAnimatronic, setAnim] = useState(false);

  const bom = [
    {cat:"Nodes",item:"GPIO-8IO",qty:rooms},
    {cat:"Nodes",item:"RELAY-8CH",qty:rooms},
    {cat:"Nodes",item:"DMX Node",qty:rooms},
    {cat:"Nodes",item:"Audio Node (RPi5)",qty:Math.ceil(rooms/3.5)},
    {cat:"Nodes",item:"Video Node (RPi5)",qty:Math.ceil(rooms/5)},
    ...(hasAnimatronic?[{cat:"Nodes",item:"Serial Bridge",qty:Math.ceil(rooms/7)}]:[]),
    {cat:"Infra",item:"Runtime Server (Mini PC)",qty:1},
    {cat:"Infra",item:"OCC Touch Panel",qty:1},
    {cat:"Infra",item:rooms*3+10<=24?"24-port Switch":"48-port Switch",qty:rooms*3+10<=48?1:2},
    {cat:"Infra",item:"24V PSU",qty:Math.ceil(rooms/10)},
    {cat:"Infra",item:"12V PSU",qty:1},
    {cat:"Infra",item:"Server Rack",qty:1},
    {cat:"Fixtures",item:"LED Par (6ch wash)",qty:rooms*parsPerRoom},
    ...(hasStrobe?[{cat:"Fixtures",item:"Strobe (4ch)",qty:rooms}]:[]),
    ...(hasPixel?[{cat:"Fixtures",item:"Pixel Bar (120ch)",qty:Math.ceil(rooms/2)}]:[]),
    ...(hasFog?[{cat:"Fixtures",item:"Fog Machine",qty:Math.ceil(rooms/3)}]:[]),
    {cat:"Props",item:"Mag Lock (fail-safe)",qty:rooms},
    {cat:"Sensors",item:"PIR Motion Sensor",qty:rooms*2},
    {cat:"Sensors",item:"Door Sensor (reed)",qty:rooms},
    {cat:"Controls",item:"E-Stop Button (N/C)",qty:rooms},
    {cat:"Cabling",item:"CAT6 cable (305m box)",qty:Math.ceil(rooms/10)},
    {cat:"Cabling",item:"Junction Box (IP65)",qty:rooms},
  ];

  return <div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
      <div>
        <label style={{fontSize:10,color:DIM,fontFamily:MONO,display:"block",marginBottom:4}}>ROOMS</label>
        <input type="number" min={1} max={50} value={rooms} onChange={e=>setRooms(Math.max(1,parseInt(e.target.value)||1))}
          style={{width:"100%",background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"8px",borderRadius:4,fontFamily:MONO,fontSize:16,fontWeight:700}} />
      </div>
      <div>
        <label style={{fontSize:10,color:DIM,fontFamily:MONO,display:"block",marginBottom:4}}>PARS / ROOM</label>
        <input type="number" min={1} max={8} value={parsPerRoom} onChange={e=>setPars(Math.max(1,parseInt(e.target.value)||1))}
          style={{width:"100%",background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"8px",borderRadius:4,fontFamily:MONO,fontSize:16,fontWeight:700}} />
      </div>
    </div>
    <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
      {[[hasStrobe,setStrobe,"Strobes"],[hasFog,setFog,"Fog"],[hasPixel,setPixel,"Pixel Bars"],[hasAnimatronic,setAnim,"Animatronics"]].map(([v,s,l])=>
        <label key={l} style={{fontSize:11,color:TEXT,fontFamily:MONO,cursor:"pointer"}}><input type="checkbox" checked={v} onChange={e=>s(e.target.checked)} style={{marginRight:5}} />{l}</label>
      )}
    </div>
    <div style={{background:BG,borderRadius:6,overflow:"hidden",border:`1px solid ${BORDER}`}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
        <thead><tr style={{background:SURFACE2}}>
          {["Category","Item","Qty"].map(h=><th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:9,color:DIM,fontWeight:700,borderBottom:`1px solid ${BORDER}`}}>{h}</th>)}
        </tr></thead>
        <tbody>{bom.map((b,i)=><tr key={i} style={{borderBottom:`1px solid ${BORDER}15`}}>
          <td style={{padding:"5px 10px",color:DIM}}>{b.cat}</td>
          <td style={{padding:"5px 10px",color:TEXT}}>{b.item}</td>
          <td style={{padding:"5px 10px",color:BLUE,fontWeight:700}}>{b.qty}</td>
        </tr>)}</tbody>
      </table>
    </div>
    <div style={{marginTop:8,fontSize:10,color:DIM,fontFamily:MONO}}>{bom.reduce((a,b)=>a+b.qty,0)} total items across {new Set(bom.map(b=>b.cat)).size} categories</div>
  </div>;
}

// ═══ CABLE ESTIMATOR ═══
function CableTool() {
  const [roomDistances, setRD] = useState([8,15,22,28,35,40]);
  const addRoom = () => setRD([...roomDistances, 30]);
  const update = (i,v) => setRD(roomDistances.map((d,j)=>j===i?v:d));
  const remove = (i) => setRD(roomDistances.filter((_,j)=>j!==i));

  const nodesPerRoom = 3; // GPIO + Relay + DMX
  const totalCable = roomDistances.reduce((a,d)=>a+d*nodesPerRoom,0);
  const rackInternal = 10; // audio/video/serial internal
  const grandTotal = totalCable + rackInternal;
  const boxes = Math.ceil(grandTotal / 305);

  return <div>
    <div style={{fontSize:10,color:DIM,fontFamily:MONO,marginBottom:10}}>Enter distance from server rack to each room (meters). 3 cables per room (GPIO + Relay + DMX).</div>
    {roomDistances.map((d,i)=><div key={i} style={{display:"flex",gap:8,marginBottom:4,alignItems:"center"}}>
      <span style={{fontSize:10,color:DIM,fontFamily:MONO,width:70}}>Room {i+1}</span>
      <input type="number" value={d} onChange={e=>update(i,parseInt(e.target.value)||0)}
        style={{width:80,background:BG,border:`1px solid ${BORDER}`,color:TEXT,padding:"4px 8px",borderRadius:3,fontFamily:MONO,fontSize:12}} />
      <span style={{fontSize:10,color:DIM,fontFamily:MONO}}>m × 3 cables = {d*3}m</span>
      <button onClick={()=>remove(i)} style={{background:"transparent",border:"none",color:RED,cursor:"pointer",fontSize:12,marginLeft:"auto"}}>✕</button>
    </div>)}
    <button onClick={addRoom} style={{background:"transparent",border:`1px dashed ${BORDER}`,color:BLUE,padding:"4px 12px",borderRadius:3,fontFamily:MONO,fontSize:10,cursor:"pointer",marginTop:4,marginBottom:14}}>+ Add Room</button>
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
      <div style={{background:BG,borderRadius:6,padding:12,border:`1px solid ${BORDER}`,textAlign:"center"}}>
        <div style={{fontSize:24,fontWeight:700,fontFamily:MONO,color:BLUE}}>{grandTotal}m</div>
        <div style={{fontSize:9,color:DIM,fontFamily:MONO}}>TOTAL CABLE</div>
      </div>
      <div style={{background:BG,borderRadius:6,padding:12,border:`1px solid ${BORDER}`,textAlign:"center"}}>
        <div style={{fontSize:24,fontWeight:700,fontFamily:MONO,color:AMBER}}>{boxes}</div>
        <div style={{fontSize:9,color:DIM,fontFamily:MONO}}>305m BOXES</div>
      </div>
      <div style={{background:BG,borderRadius:6,padding:12,border:`1px solid ${BORDER}`,textAlign:"center"}}>
        <div style={{fontSize:24,fontWeight:700,fontFamily:MONO,color:GREEN}}>{roomDistances.length * 3 + 5}</div>
        <div style={{fontSize:9,color:DIM,fontFamily:MONO}}>CABLE RUNS</div>
      </div>
    </div>
  </div>;
}

// ═══ SHOW FILE VALIDATOR ═══
function ValidatorTool() {
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const validate = (data) => {
    const errors = [], warnings = [], passes = [];
    if(!data.schema) errors.push("Missing schema version");
    else passes.push(`Schema: ${data.schema}`);
    if(!data.projectName) warnings.push("No project name set");
    else passes.push(`Project: ${data.projectName}`);
    if(!data.nodes?.length) errors.push("No nodes defined");
    else passes.push(`${data.nodes.length} nodes defined`);
    if(!data.sequences?.length) errors.push("No sequences defined");
    else passes.push(`${data.sequences.length} sequences`);
    const hasEstop = data.sequences?.some(s=>s.name?.toLowerCase().includes("stop"));
    if(!hasEstop) warnings.push("No E-Stop sequence found — safety concern");
    else passes.push("E-Stop sequence present");
    const hasReset = data.sequences?.some(s=>s.name?.toLowerCase().includes("reset"));
    if(!hasReset) warnings.push("No Reset sequence found");
    else passes.push("Reset sequence present");
    if(data.outputs?.length) passes.push(`${data.outputs.length} outputs`);
    if(data.inputs?.length) passes.push(`${data.inputs.length} inputs`);
    if(data.fixtures?.length) passes.push(`${data.fixtures.length} fixtures across ${new Set(data.fixtures.map(f=>f.u)).size} universes`);
    if(data.scenes?.length) passes.push(`${data.scenes.length} scenes`);
    if(data.media?.length) passes.push(`${data.media.length} media files`);
    if(data.triggers?.length) passes.push(`${data.triggers.length} triggers`);
    else warnings.push("No triggers defined — sequences must be started manually");
    if(data.touchItems?.length) passes.push(`${data.touchItems.length} OCC touch items`);
    else warnings.push("No touch panel items — OCC will be empty");
    // Check for duplicate IPs
    const ips = data.nodes?.map(n=>n.ip) || [];
    const dupIps = ips.filter((ip,i)=>ips.indexOf(ip)!==i);
    if(dupIps.length) errors.push(`Duplicate IPs: ${[...new Set(dupIps)].join(", ")}`);
    else if(ips.length) passes.push("No duplicate IPs");
    setResult({errors,warnings,passes});
  };

  const handleFile = (e) => {
    const f = e.target.files[0]; if(!f) return;
    setFileName(f.name);
    const r = new FileReader();
    r.onload = ev => { try { validate(JSON.parse(ev.target.result)); } catch(err) { setResult({errors:["Invalid JSON: "+err.message],warnings:[],passes:[]}) } };
    r.readAsText(f);
  };

  return <div>
    <input ref={fileRef} type="file" accept=".ishow,.json" onChange={handleFile} style={{display:"none"}} />
    <button onClick={()=>fileRef.current?.click()} style={{width:"100%",padding:"20px",background:BG,border:`2px dashed ${BORDER}`,borderRadius:8,color:BLUE,fontFamily:MONO,fontSize:13,fontWeight:600,cursor:"pointer",marginBottom:12,transition:"border-color .2s"}}
      onMouseOver={e=>e.target.style.borderColor=BLUE} onMouseOut={e=>e.target.style.borderColor=BORDER}>
      📁 Drop or click to upload .ishow file
    </button>
    {fileName && <div style={{fontSize:10,color:DIM,fontFamily:MONO,marginBottom:10}}>File: {fileName}</div>}
    {result && <div>
      {result.errors.map((e,i)=><div key={`e${i}`} style={{padding:"6px 10px",marginBottom:4,borderRadius:4,background:`${RED}10`,border:`1px solid ${RED}30`,color:RED,fontFamily:MONO,fontSize:11}}>✗ {e}</div>)}
      {result.warnings.map((w,i)=><div key={`w${i}`} style={{padding:"6px 10px",marginBottom:4,borderRadius:4,background:`${AMBER}10`,border:`1px solid ${AMBER}30`,color:AMBER,fontFamily:MONO,fontSize:11}}>⚠ {w}</div>)}
      {result.passes.map((p,i)=><div key={`p${i}`} style={{padding:"6px 10px",marginBottom:4,borderRadius:4,background:`${GREEN}10`,border:`1px solid ${GREEN}30`,color:GREEN,fontFamily:MONO,fontSize:11}}>✓ {p}</div>)}
      <div style={{marginTop:10,padding:10,borderRadius:6,background:result.errors.length?`${RED}08`:`${GREEN}08`,border:`1px solid ${result.errors.length?RED:GREEN}20`,textAlign:"center"}}>
        <span style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:result.errors.length?RED:GREEN}}>{result.errors.length?`${result.errors.length} ERROR${result.errors.length>1?"S":""}`:result.warnings.length?`VALID (${result.warnings.length} warning${result.warnings.length>1?"s":""})`:"✓ ALL CHECKS PASSED"}</span>
      </div>
    </div>}
  </div>;
}

// ═══ MAIN APP ═══
// ═══ PASSWORD GATE ═══
const PASS_HASH = "a3f2c8e1"; // simple hash of immerse@design
function simpleHash(s){let h=0;for(let i=0;i<s.length;i++){h=((h<<5)-h)+s.charCodeAt(i);h|=0}return(h>>>0).toString(16).slice(0,8)}

function LoginGate({children}) {
  const [authed, setAuthed] = useState(()=>sessionStorage.getItem("immerse_auth")==="1");
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const submit = (e) => {
    e?.preventDefault();
    if(pw === "immerse@design") {
      sessionStorage.setItem("immerse_auth","1");
      setAuthed(true);
    } else {
      setError(true);
      setShake(true);
      setTimeout(()=>setShake(false), 500);
      setTimeout(()=>setError(false), 2000);
    }
  };

  if(authed) return children;

  return <div style={{background:BG,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:SANS}}>
    <style>{`
      @keyframes fadeIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      @keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }
      @keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:1} }
      @keyframes glow { 0%,100%{box-shadow:0 0 20px ${BLUE}10} 50%{box-shadow:0 0 40px ${BLUE}20} }
    `}</style>
    <div style={{animation:"fadeIn 0.6s ease-out",textAlign:"center",maxWidth:380,width:"100%",padding:"0 24px"}}>
      {/* Logo */}
      <div style={{marginBottom:40}}>
        <div style={{width:48,height:48,margin:"0 auto 16px",background:`${BLUE}15`,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",animation:"glow 3s ease-in-out infinite"}}>
          <div style={{width:20,height:20,background:BLUE,clipPath:"polygon(50% 0%,100% 50%,50% 100%,0% 50%)"}} />
        </div>
        <div style={{fontFamily:MONO,fontWeight:700,fontSize:24,color:TEXT,letterSpacing:".03em"}}>IMMERSE</div>
        <div style={{fontFamily:MONO,fontSize:11,color:BLUE,fontWeight:600,letterSpacing:".12em",marginTop:2}}>CERTIFIED DESIGNER PORTAL</div>
      </div>

      {/* Login card */}
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:32,animation:shake?"shake 0.4s ease":"none"}}>
        <div style={{fontFamily:MONO,fontSize:10,color:DIM,letterSpacing:".08em",textTransform:"uppercase",marginBottom:16}}>Authorized Access Only</div>

        <form onSubmit={submit}>
          <div style={{position:"relative",marginBottom:16}}>
            <input
              type="password"
              value={pw}
              onChange={e=>{setPw(e.target.value);setError(false)}}
              placeholder="Enter access code"
              autoFocus
              style={{
                width:"100%",
                background:BG,
                border:`1px solid ${error?RED:pw?BLUE:BORDER}`,
                color:TEXT,
                padding:"14px 16px",
                borderRadius:6,
                fontFamily:MONO,
                fontSize:14,
                outline:"none",
                transition:"border-color 0.2s",
                boxSizing:"border-box",
              }}
            />
          </div>

          {error && <div style={{
            fontSize:11,fontFamily:MONO,color:RED,marginBottom:12,
            padding:"6px 10px",background:`${RED}10`,borderRadius:4,border:`1px solid ${RED}30`
          }}>Access denied — invalid code</div>}

          <button type="submit" style={{
            width:"100%",
            background:BLUE,
            color:"#fff",
            border:"none",
            padding:"12px",
            borderRadius:6,
            fontFamily:MONO,
            fontSize:12,
            fontWeight:700,
            letterSpacing:".05em",
            cursor:"pointer",
            transition:"background 0.15s",
          }}
            onMouseOver={e=>e.target.style.background="#1a7ee6"}
            onMouseOut={e=>e.target.style.background=BLUE}
          >AUTHENTICATE</button>
        </form>
      </div>

      <div style={{marginTop:24,fontFamily:MONO,fontSize:9,color:`${DIM}80`,lineHeight:1.6}}>
        This portal is restricted to IMMERSE certified designers.<br/>
        Contact your account manager for access credentials.
      </div>

      <div style={{marginTop:32,fontFamily:MONO,fontSize:9,color:`${DIM}50`}}>
        designers.immersecontrol.com
      </div>
    </div>
  </div>;
}

export default function App() {
  const [page, setPage] = useState("home");
  const [activeTool, setActiveTool] = useState(null);

  const nav = (p) => { setPage(p); setActiveTool(null); window.scrollTo(0,0); };

  return <LoginGate><div style={{background:BG,color:TEXT,minHeight:"100vh",fontFamily:SANS}}>
    {/* NAV BAR */}
    <nav style={{position:"sticky",top:0,zIndex:100,background:`${BG}ee`,backdropFilter:"blur(12px)",borderBottom:`1px solid ${BORDER}`,padding:"0 24px"}}>
      <div style={{maxWidth:1200,margin:"0 auto",display:"flex",alignItems:"center",height:52,gap:24}}>
        <div onClick={()=>nav("home")} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:8,height:8,background:BLUE,clipPath:"polygon(50% 0%,100% 50%,50% 100%,0% 50%)"}} />
          <span style={{fontFamily:MONO,fontWeight:700,fontSize:14,color:TEXT,letterSpacing:".03em"}}>IMMERSE</span>
          <span style={{fontFamily:MONO,fontSize:10,color:BLUE,fontWeight:600}}>DESIGNERS</span>
        </div>
        <div style={{flex:1}} />
        {[["home","Dashboard"],["shows","Example Shows"],["tools","Designer Tools"],["docs","Documentation"]].map(([k,l])=>
          <button key={k} onClick={()=>nav(k)} style={{background:"none",border:"none",color:page===k?BLUE:DIM,fontFamily:MONO,fontSize:11,fontWeight:page===k?700:500,cursor:"pointer",letterSpacing:".03em",padding:"4px 0",borderBottom:page===k?`2px solid ${BLUE}`:"2px solid transparent",transition:"all .15s"}}>{l}</button>
        )}
      </div>
    </nav>

    <div style={{maxWidth:1200,margin:"0 auto",padding:"0 24px"}}>

      {/* ═══ HOME / DASHBOARD ═══ */}
      {page === "home" && <div>
        <div style={{padding:"60px 0 40px"}}>
          <div style={{fontSize:10,fontFamily:MONO,color:BLUE,fontWeight:600,letterSpacing:".12em",marginBottom:8}}>CERTIFIED DESIGNER PORTAL</div>
          <h1 style={{fontSize:36,fontWeight:700,fontFamily:MONO,lineHeight:1.1,marginBottom:12}}>Design. Deploy.<br/><span style={{color:BLUE}}>Immerse.</span></h1>
          <p style={{fontSize:15,color:DIM,maxWidth:500,lineHeight:1.6}}>Everything you need to design IMMERSE show control systems. Example shows, interactive tools, documentation, and resources for certified designers.</p>
        </div>

        {/* Quick Stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:40}}>
          {[{l:"Example Shows",v:"3",c:BLUE},{l:"Designer Tools",v:"6",c:AMBER},{l:"Node Types",v:"6",c:GREEN},{l:".ishow Schema",v:"0.5.0",c:DIM}].map(s=>
            <div key={s.l} style={{background:SURFACE,borderRadius:8,padding:20,border:`1px solid ${BORDER}`}}>
              <Stat label={s.l} value={s.v} color={s.c} />
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{fontSize:10,fontFamily:MONO,color:DIM,fontWeight:600,letterSpacing:".08em",marginBottom:12}}>QUICK ACTIONS</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:40}}>
          {[["🎪","Browse Example Shows","shows"],["🔧","Open Designer Tools","tools"],["📖","Read Documentation","docs"]].map(([icon,label,target])=>
            <button key={target} onClick={()=>nav(target)} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,padding:20,cursor:"pointer",textAlign:"left",transition:"all .15s"}}
              onMouseOver={e=>{e.currentTarget.style.borderColor=BLUE;e.currentTarget.style.background=SURFACE2}} onMouseOut={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.background=SURFACE}}>
              <div style={{fontSize:24,marginBottom:8}}>{icon}</div>
              <div style={{fontFamily:MONO,fontSize:12,fontWeight:600,color:TEXT}}>{label}</div>
            </button>
          )}
        </div>
      </div>}

      {/* ═══ EXAMPLE SHOWS ═══ */}
      {page === "shows" && <div style={{paddingTop:40}}>
        <div style={{fontSize:10,fontFamily:MONO,color:BLUE,fontWeight:600,letterSpacing:".12em",marginBottom:8}}>REFERENCE DESIGNS</div>
        <h2 style={{fontSize:28,fontWeight:700,fontFamily:MONO,marginBottom:8}}>Example Shows</h2>
        <p style={{color:DIM,fontSize:14,marginBottom:32,maxWidth:500}}>Three production-ready show files spanning different scales. Download, study, and use as starting templates.</p>

        {SHOWS.map((show,i) => <div key={i} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:24,marginBottom:20,transition:"border-color .2s"}}
          onMouseOver={e=>e.currentTarget.style.borderColor=show.color} onMouseOut={e=>e.currentTarget.style.borderColor=BORDER}>
          <div style={{display:"flex",alignItems:"flex-start",gap:20,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:280}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <Badge color={show.color}>{show.type.toUpperCase()}</Badge>
                <Badge color={DIM}>{show.rooms} ROOMS</Badge>
              </div>
              <h3 style={{fontSize:22,fontWeight:700,fontFamily:MONO,color:TEXT,marginBottom:8}}>{show.name}</h3>
              <p style={{fontSize:13,color:DIM,lineHeight:1.6,marginBottom:16}}>{show.desc}</p>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <a href={["/downloads/Prison_Break_4Room.zip","/downloads/Carnival_of_Screams.zip","/downloads/Threshold_20Room.zip"][i]} download style={{background:`${show.color}15`,border:`1px solid ${show.color}40`,color:show.color,fontFamily:MONO,fontSize:11,fontWeight:600,padding:"8px 16px",borderRadius:4,cursor:"pointer",textDecoration:"none"}}>📥 Download Show Pack</a>
                <span style={{background:`${BLUE}15`,border:`1px solid ${BLUE}40`,color:BLUE,fontFamily:MONO,fontSize:11,fontWeight:600,padding:"8px 16px",borderRadius:4,cursor:"pointer"}}>📋 Included in Pack</span>
                <span style={{background:`${GREEN}15`,border:`1px solid ${GREEN}40`,color:GREEN,fontFamily:MONO,fontSize:11,fontWeight:600,padding:"8px 16px",borderRadius:4}}>📄 Included in Pack</span>
                <span style={{background:`${AMBER}15`,border:`1px solid ${AMBER}40`,color:AMBER,fontFamily:MONO,fontSize:11,fontWeight:600,padding:"8px 16px",borderRadius:4}}>📊 Included in Pack</span>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,minWidth:200}}>
              {show.stats.map(s=><div key={s.l} style={{textAlign:"center",padding:8,background:BG,borderRadius:4,border:`1px solid ${BORDER}`}}>
                <div style={{fontSize:18,fontWeight:700,fontFamily:MONO,color:show.color}}>{s.v}</div>
                <div style={{fontSize:8,color:DIM,fontFamily:MONO,letterSpacing:".05em"}}>{s.l}</div>
              </div>)}
            </div>
          </div>
        </div>)}
      </div>}

      {/* ═══ DESIGNER TOOLS ═══ */}
      {page === "tools" && <div style={{paddingTop:40}}>
        <div style={{fontSize:10,fontFamily:MONO,color:BLUE,fontWeight:600,letterSpacing:".12em",marginBottom:8}}>INTERACTIVE TOOLS</div>
        <h2 style={{fontSize:28,fontWeight:700,fontFamily:MONO,marginBottom:8}}>Designer Tools</h2>
        <p style={{color:DIM,fontSize:14,marginBottom:32,maxWidth:500}}>Interactive calculators, planners, and validators to streamline your design workflow.</p>

        {!activeTool && <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:16}}>
          {TOOLS.map(tool => <button key={tool.id} onClick={()=>setActiveTool(tool.id)}
            style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,padding:20,cursor:"pointer",textAlign:"left",transition:"all .15s"}}
            onMouseOver={e=>{e.currentTarget.style.borderColor=BLUE;e.currentTarget.style.background=SURFACE2}} onMouseOut={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.background=SURFACE}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <span style={{fontSize:28}}>{tool.icon}</span>
              <Badge color={BLUE}>{tool.tag}</Badge>
            </div>
            <div style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:TEXT,marginBottom:6}}>{tool.name}</div>
            <div style={{fontSize:12,color:DIM,lineHeight:1.5}}>{tool.desc}</div>
          </button>)}
        </div>}

        {activeTool && <div>
          <button onClick={()=>setActiveTool(null)} style={{background:"transparent",border:"none",color:BLUE,fontFamily:MONO,fontSize:11,cursor:"pointer",marginBottom:16,padding:0}}>← Back to all tools</button>
          <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:24}}>
            <h3 style={{fontFamily:MONO,fontSize:18,fontWeight:700,marginBottom:16}}>{TOOLS.find(t=>t.id===activeTool)?.icon} {TOOLS.find(t=>t.id===activeTool)?.name}</h3>
            {activeTool === "calc" && <NodeCalcTool />}
            {activeTool === "dmx" && <DMXPlannerTool />}
            {activeTool === "cable" && <CableTool />}
            {activeTool === "bom" && <BOMTool />}
            {activeTool === "valid" && <ValidatorTool />}
            {activeTool === "patch" && <div>
              <p style={{color:DIM,fontSize:13,lineHeight:1.6,marginBottom:16}}>The Network Patching Sheet is a standalone HTML tool. Open it in your browser, fill in your network details, and print to PDF for venue handoff.</p>
              <a href="/downloads/IMMERSE_Network_Patching_Sheet.html" download style={{display:"inline-block",background:`${BLUE}15`,border:`1px solid ${BLUE}40`,color:BLUE,fontFamily:MONO,fontSize:12,fontWeight:600,padding:"12px 24px",borderRadius:4,cursor:"pointer",textDecoration:"none"}}>📥 Download Patching Sheet (HTML)</a>
            </div>}
          </div>
        </div>}
      </div>}

      {/* ═══ DOCUMENTATION ═══ */}
      {page === "docs" && <div style={{paddingTop:40,paddingBottom:60}}>
        <div style={{fontSize:10,fontFamily:MONO,color:BLUE,fontWeight:600,letterSpacing:".12em",marginBottom:8}}>REFERENCE</div>
        <h2 style={{fontSize:28,fontWeight:700,fontFamily:MONO,marginBottom:32}}>Documentation</h2>

        <div style={{fontSize:12,fontFamily:MONO,fontWeight:700,color:TEXT,marginBottom:12}}>Node Types & Specifications</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:32}}>
          {NODE_TYPES.map(nt=><div key={nt.name} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,padding:16,transition:"border-color .15s"}}
            onMouseOver={e=>e.currentTarget.style.borderColor=nt.color} onMouseOut={e=>e.currentTarget.style.borderColor=BORDER}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:nt.color}} />
              <span style={{fontFamily:MONO,fontSize:13,fontWeight:700,color:TEXT}}>{nt.name}</span>
              <Badge color={nt.color}>{nt.role.toUpperCase()}</Badge>
            </div>
            <p style={{fontSize:12,color:DIM,lineHeight:1.5,marginBottom:8}}>{nt.desc}</p>
            <div style={{fontSize:10,fontFamily:MONO,color:`${DIM}aa`}}>
              <span style={{marginRight:12}}>HW: {nt.hw}</span>
              <span>PWR: {nt.power}</span>
            </div>
          </div>)}
        </div>

        <div style={{fontSize:12,fontFamily:MONO,fontWeight:700,color:TEXT,marginBottom:12}}>.ishow File Format</div>
        <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,padding:16,marginBottom:32,fontFamily:MONO,fontSize:11,color:DIM,lineHeight:1.8}}>
          <div style={{color:TEXT,fontWeight:600,marginBottom:8}}>Schema 0.5.0 — Top-level fields:</div>
          {["schema — version string (\"0.5.0\")", "projectName — show title", "nodes[] — GPIO, Relay, DMX, Audio, Video, Serial",
            "sequences[] — timed or logical event lists", "variables[] — name, type, default value",
            "outputs[] — relay channel assignments", "inputs[] — GPIO sensor assignments",
            "media[] — audio/video file inventory", "protocols[] — UDP, RS-232, Modbus",
            "triggers[] — auto-start conditions", "scenes[] — DMX lighting presets",
            "fixtures[] — DMX fixture patch", "groups[] — fixture groups",
            "touchItems[] — OCC layout", "timelines[] — lighting timelines", "blocks[] — timeline blocks"
          ].map(f=><div key={f}><span style={{color:BLUE}}>•</span> {f}</div>)}
        </div>

        <div style={{fontSize:12,fontFamily:MONO,fontWeight:700,color:TEXT,marginBottom:12}}>IP Address Convention</div>
        <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,overflow:"hidden",marginBottom:32}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
            <thead><tr style={{background:SURFACE2}}>
              {["Range","Purpose","Example"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:9,color:DIM,fontWeight:700,borderBottom:`1px solid ${BORDER}`}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {[["10.0.0.x","Infrastructure (switches, routers)","10.0.0.1 — Primary switch"],
                ["10.0.1.x","Servers & OCC","10.0.1.1 — Runtime, 10.0.1.2 — OCC"],
                ["10.0.2.10–29","GPIO nodes","10.0.2.10 — first room GPIO"],
                ["10.0.2.40–59","Relay nodes","10.0.2.40 — first room Relay"],
                ["10.0.3.x","DMX nodes","10.0.3.10 — first room DMX (U1)"],
                ["10.0.4.x","Audio nodes","10.0.4.10 — first audio RPi"],
                ["10.0.5.x","Video nodes","10.0.5.10 — first video RPi"],
                ["10.0.6.x","Serial bridges","10.0.6.10 — first PLC bridge"],
              ].map((r,i)=><tr key={i} style={{borderBottom:`1px solid ${BORDER}15`}}>
                <td style={{padding:"6px 12px",color:BLUE,fontWeight:600}}>{r[0]}</td>
                <td style={{padding:"6px 12px",color:TEXT}}>{r[1]}</td>
                <td style={{padding:"6px 12px",color:DIM}}>{r[2]}</td>
              </tr>)}
            </tbody>
          </table>
        </div>

        <div style={{fontSize:12,fontFamily:MONO,fontWeight:700,color:TEXT,marginBottom:12}}>Downloads</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
          {[["IMMERSE Studio","Desktop app (.exe)","Download v1.0",BLUE,"#"],
            ["Patching Sheet","Network config tool (HTML)","\u2193 Download",GREEN,"/downloads/IMMERSE_Network_Patching_Sheet.html"],
            ["Example Shows","All 3 show packs","\u2193 Download",AMBER,"/downloads/"]
          ].map(([t,d,b,c,href])=><div key={t} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,padding:16}}>
            <div style={{fontFamily:MONO,fontSize:12,fontWeight:700,color:TEXT,marginBottom:4}}>{t}</div>
            <div style={{fontSize:11,color:DIM,marginBottom:12}}>{d}</div>
            <a href={href} download={href!=="#"&&!href.endsWith("/")} style={{display:"inline-block",background:`${c}15`,border:`1px solid ${c}40`,color:c,fontFamily:MONO,fontSize:10,fontWeight:600,padding:"6px 14px",borderRadius:3,cursor:"pointer",textDecoration:"none"}}>{b}</a>
            {t==="IMMERSE Studio"&&<div style={{fontSize:9,color:DIM,fontFamily:MONO,marginTop:6}}>169 MB — hosted on GitHub Releases</div>}
          </div>)}
        </div>
      </div>}

    </div>

    {/* FOOTER */}
    <footer style={{borderTop:`1px solid ${BORDER}`,marginTop:60,padding:"20px 24px"}}>
      <div style={{maxWidth:1200,margin:"0 auto",display:"flex",justifyContent:"space-between",fontFamily:MONO,fontSize:10,color:DIM}}>
        <span>© 2026 IMMERSE Control — Certified Designer Portal</span>
        <span>designers.immersecontrol.com</span>
      </div>
    </footer>
  </div></LoginGate>;
}
